import React, { FC, useEffect, useMemo, useState } from 'react';
import { Autocomplete, Button, TextField } from '@mui/material';
import { useSelector } from 'react-redux';
import useLocales from 'hooks/useLocales';
import { IJobIndustry } from 'core/interface/job';
import { RootState } from 'redux/store';
import { ProfileService } from 'services/profile/profile';
import { useDebounce } from 'helpers/useDebounce';
import {
  normalizeJobIndustriesFromApi
} from 'utils/jobIndustriesLocations';
import {
  EditableShell,
  useInlineEditHint
} from 'components/companyJobDetail/inlineFields';

type InlineIndustriesProps = {
  industries: Array<IJobIndustry | number>;
  disabled?: boolean;
  onSave: (industries: IJobIndustry[]) => Promise<boolean>;
};

const InlineIndustries: FC<InlineIndustriesProps> = ({
  industries,
  disabled,
  onSave
}) => {
  const { t } = useLocales();
  const hint = useInlineEditHint();
  const searchResults = useSelector(
    (state: RootState) => state.industries?.industry || []
  );
  const resolved = useMemo(
    () => normalizeJobIndustriesFromApi(industries || [], []),
    [industries]
  );
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<IJobIndustry[]>(resolved);
  const [query, setQuery] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const debouncedQuery = useDebounce(query, 400);

  useEffect(() => {
    if (!editing) {
      setDraft(resolved);
      setQuery('');
      setListOpen(false);
    }
  }, [resolved, editing]);

  useEffect(() => {
    if (!editing) {
      return;
    }
    ProfileService.getIndustries(debouncedQuery);
  }, [editing, debouncedQuery]);

  const options = useMemo(() => {
    const selectedIds = new Set(draft.map((item) => item.id));
    const normalizedQuery = query.trim().toLowerCase();

    return (Array.isArray(searchResults) ? searchResults : [])
      .filter((industry) => {
        if (selectedIds.has(industry.id)) {
          return false;
        }
        if (!normalizedQuery) {
          return true;
        }
        return industry.name.toLowerCase().includes(normalizedQuery);
      })
      .slice(0, 50)
      .map((industry) => ({
        id: industry.id,
        name: industry.name
      }));
  }, [searchResults, draft, query]);

  const handleSave = async () => {
    if (!draft.length) {
      return;
    }
    const ok = await onSave(draft);
    if (ok) {
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <div className="company-job-detail__industry-editor is-editing">
        <Autocomplete
          multiple
          fullWidth
          disableClearable
          open={listOpen}
          onOpen={() => setListOpen(true)}
          onClose={() => setListOpen(false)}
          className="company-job-detail__industry-autocomplete"
          options={options}
          value={draft}
          inputValue={query}
          getOptionLabel={(option) => option.name || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={(items) => items}
          onChange={(_, next, reason) => {
            setDraft(next);
            if (reason === 'selectOption') {
              setQuery('');
              setListOpen(false);
            }
          }}
          onInputChange={(_, value, reason) => {
            if (reason === 'reset') {
              return;
            }
            setQuery(value);
            if (reason === 'input') {
              setListOpen(true);
            }
          }}
          componentsProps={{
            popper: {
              placement: 'bottom-start',
              modifiers: [
                {
                  name: 'offset',
                  options: { offset: [0, 6] }
                }
              ]
            },
            paper: {
              sx: { mt: 0.5 }
            }
          }}
          ListboxProps={{ style: { maxHeight: 220 } }}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              size="small"
              placeholder={
                draft.length ? t('Add another industry') : t('Search industries')
              }
              helperText={
                draft.length ? undefined : t('At least one industry is required')
              }
            />
          )}
        />
        <div className="company-job-detail__edit-actions">
          <Button size="small" onClick={() => setEditing(false)}>
            {t('Cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={disabled || !draft.length}
          >
            {t('Save')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <EditableShell
      editing={false}
      disabled={disabled}
      hint={hint}
      onStart={() => setEditing(true)}
    >
      {resolved.length ? (
        <span>{resolved.map((item) => item.name).join(', ')}</span>
      ) : (
        <span className="company-job-detail__placeholder">{hint}</span>
      )}
    </EditableShell>
  );
};

export default InlineIndustries;
